import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  inject,
  input,
  model,
  viewChild,
  output,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { outputFromObservable } from '@angular/core/rxjs-interop';
import { AgGridModule } from 'ag-grid-angular';
import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  CellClickedEvent,
  CellValueChangedEvent,
  CheckboxSelectionCallbackParams,
  ColumnApi,
  FirstDataRenderedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IRowNode,
  IServerSideGetRowsParams,
  RowDragMoveEvent,
  RowNode,
  RowSelectedEvent,
} from 'ag-grid-community';
import { CheckboxSelectionCallback, ColDef, ColGroupDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { v4 as newGuid } from 'uuid';

import { AggFunc, ColourNames } from '../../../constants';
import { BaseComponent } from '../../base/base.component';
import { DeleteModalComponent } from '../../modals/delete-modal/delete-modal.component';
import {
  ColumnKeyMap,
  ColumnResized,
  GridChartCellClicked,
  GridRowSelect,
  GridState,
  IdDescription,
  ScrollChanged,
  UserPreference,
} from '../../../models';
import {
  BaseImportService,
  FormatService,
  MenuService,
  ModalService,
  NotificationService,
  UserPreferenceService,
  UtilityService,
} from '../../../services';
import { FormlyService } from '../../../services/component-services/formly.service';
import { FormService } from '../../../services/component-services/form.service';
import { GridService } from '../../../services/component-services/grid.service';
import { ignoreModalError } from '../../../helpers';

@Component({
  selector: 'app-grid',
  templateUrl: './grid.component.html',
  styleUrl: './grid.component.scss',
  imports: [NgTemplateOutlet, AgGridModule],
})
export class GridComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly modalService = inject(ModalService);
  private readonly gridService = inject(GridService);
  private readonly formService = inject(FormService);
  private readonly formatService = inject(FormatService);
  private readonly menuService = inject(MenuService);
  private readonly notificationService = inject(NotificationService);
  private readonly userPreferenceService = inject(UserPreferenceService);
  private readonly utilityService = inject(UtilityService);
  private readonly formlyService = inject(FormlyService);

  protected save$ = new Subject<void>();

  public readonly stateKey = model<string>();
  public readonly columnDefs = input<(ColDef | ColGroupDef)[]>([]);
  public readonly groupColumnHeader = input<string>();
  public readonly groupDefaultExpanded = input(0);
  public readonly rememberGroupStateWhenNewData = input(false);
  public readonly header = input<string>();
  public readonly layoutStateKey = input<string>();
  public readonly filter = input(true);
  public readonly gridCssClass = input<string>();
  public readonly pagination = input(true);
  public readonly pageSize = input(100);
  public readonly resizable = input(true);
  public readonly rowData = model<any[]>([]);
  public readonly multiSelectRows = input(true);
  public readonly multiSelectCells = input(false);
  public readonly showDelete = input(true);
  public readonly showDeleteMessage = input(true);
  public readonly showDiscard = input(true);
  public readonly showPrint = input(false);
  public readonly showCumulativeTotals = input(false);
  public readonly showLayouts = input(false);
  public readonly retainState = input(false);
  public readonly sortable = input(true);
  public readonly sizeToFit = input(false);
  public readonly suppressScrollOnNewData = input(false);
  public readonly importService = input<BaseImportService>();
  public readonly chart = input(false);
  public readonly serverSide = input(false);
  public readonly gridIndex = input<number>();
  public readonly columnKeyMap = input<ColumnKeyMap>();
  public readonly delete = output<any>();
  public readonly print = output<any>();
  public readonly edit = output<any>();
  public readonly chartCellClicked = output<GridChartCellClicked>();
  public readonly rowSelected = output<GridRowSelect>();
  public readonly scrollChanged = output<ScrollChanged>();
  public readonly columnResized = output<ColumnResized>();
  public readonly firstDataRendered = output<FirstDataRenderedEvent>();
  public readonly save = outputFromObservable<void>(this.save$);
  public readonly imported = output<void>();
  public readonly savedState = output<void>();
  public readonly serverSideSearch = output<IServerSideGetRowsParams>();

  private readonly agGridRef = viewChild.required<ElementRef>('agGrid');

  public gridOptions: GridOptions;
  public edited = false;

  public gridApi: GridApi;
  public gridColumnApi: ColumnApi;
  protected state: GridState = {} as GridState;

  protected components: any;
  protected updateFilter = true;
  protected stateLoaded = false;
  protected filterCount = 0;

  public layoutsOptions$ = new BehaviorSubject<IdDescription[]>([]);
  private layouts$ = new BehaviorSubject<UserPreference[]>([]);
  private layoutFields: FormlyFieldConfig[];
  private layoutPreferenceKey?: string;
  private defaultState: GridState | null = null;
  private readonly globalText = $localize`Global`;

  private gridId: string;
  private modalLevel: number;
  private saveStateTimer: any;

  ngOnInit() {
    const columnDefs = [...this.columnDefs()] as ColDef[];

    if (this.showPrint()) {
      columnDefs.push(
        this.gridService.button(
          (model: any) => this.print.emit(model),
          '',
          'print',
          'text-dark',
          'fa fa-print',
          undefined,
          { pinned: 'right' },
        ),
      );
    }

    if (this.showDelete() && !this.menuService.isReadOnly) {
      columnDefs.push(
        this.gridService.button(
          (model: any) => {
            if (this.showDeleteMessage()) {
              if (model.canDelete !== false) {
                this.modalService
                  .openSmall(DeleteModalComponent)
                  .modal.result.then(() => this.delete.emit(model), ignoreModalError);
              }
            } else {
              this.delete.emit(model);
            }
          },
          '',
          'delete',
          'text-danger',
          'fa fa-trash-alt',
          (model: any) => model?.canDelete !== false,
          {
            showColumnFunc: (data: any[]) => data.some((x: any) => x.canDelete !== false),
            pinned: 'right',
          },
        ),
      );
    }

    if (this.serverSide()) {
      this.columnDefs().forEach((x: ColDef) => (x.filter = undefined));
    }

    const rowDrag = columnDefs.some((x: ColDef) => x.rowDrag);

    this.gridOptions = {
      columnDefs: columnDefs,
      defaultColDef: {
        filter: this.filter() && !this.serverSide(),
        resizable: this.resizable(),
        sortable: this.sortable() && !this.serverSide(),
        suppressMenu: !this.filter(),
      },
      autoGroupColumnDef: {
        headerName: this.groupColumnHeader() ?? columnDefs.find((x: ColDef) => x.rowGroup)?.headerName,
      },
      rowClassRules: {
        'bg-requires-review': (params) => params.data?.requiresReview === 'Yes',
        'bg-do-not-sell': (params) => params.data?.doNotSell === 'Yes',
        'bg-blue': (params) => params.data?.colour === ColourNames.blue,
        'bg-indigo': (params) => params.data?.colour === ColourNames.indigo,
        'bg-purple': (params) => params.data?.colour === ColourNames.purple,
        'bg-pink': (params) => params.data?.colour === ColourNames.pink,
        'bg-red': (params) => params.data?.colour === ColourNames.red,
        'bg-orange': (params) => params.data?.colour === ColourNames.orange,
        'bg-yellow': (params) => params.data?.colour === ColourNames.yellow,
        'bg-green': (params) => params.data?.colour === ColourNames.green,
        'bg-teal': (params) => params.data?.colour === ColourNames.teal,
        'bg-cyan': (params) => params.data?.colour === ColourNames.cyan,
        'bg-gray': (params) => params.data?.colour === ColourNames.gray,
        'bg-black': (params) => params.data?.colour === ColourNames.black,
      },
      pagination: this.pagination() && !rowDrag,
      paginationPageSize: this.pageSize(),
      rowDragManaged: rowDrag,
      suppressRowClickSelection: true,
      suppressRowTransform: true,
      suppressPropertyNamesCheck: true,
      suppressAggFuncInHeader: true,
      suppressMultiRangeSelection: true,
      stopEditingWhenCellsLoseFocus: true,
      suppressColumnVirtualisation: this.sizeToFit(),
      enableRangeSelection: this.multiSelectCells(),
    };

    if (this.serverSide()) {
      this.gridOptions.rowModelType = 'serverSide';
      this.gridOptions.cacheBlockSize = this.pageSize();
      this.gridOptions.serverSideDatasource = {
        getRows: (params: IServerSideGetRowsParams) => this.serverSideSearch.emit(params),
      };
    }

    this.components = this.gridService.components;

    const checkboxRowSelect = columnDefs.find((x) => x.checkboxSelection);
    if (checkboxRowSelect) {
      this.gridOptions.rowSelection = this.multiSelectRows() ? 'multiple' : 'single';
      this.gridOptions.rowMultiSelectWithClick = true;
      this.gridOptions.isRowSelectable = (rowNode: IRowNode) =>
        (checkboxRowSelect.checkboxSelection as CheckboxSelectionCallback).call(this, {
          data: rowNode.data,
        } as CheckboxSelectionCallbackParams);
    }

    this.modalLevel = this.modalService.modalLevel;
    this.formService.save.pipe(takeUntil(this.destroy$)).subscribe((modalLevel: number) => {
      if (modalLevel === this.modalLevel && this.edited) {
        this.save$.next();
      }
    });

    this.gridId = newGuid();
    this.formService.addGrid(this.modalLevel, this.gridId, this);
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    this.clearSaveStateTimer();
    this.formService.removeGrid(this.modalLevel, this.gridId);
    this.gridApi = undefined;
    this.gridColumnApi = undefined;
  }

  ngAfterViewInit() {
    const vViewport = this.agGridRef().nativeElement.querySelector('.ag-body-viewport') as HTMLElement;
    const hViewport = this.agGridRef().nativeElement.querySelector(
      '.ag-body-horizontal-scroll-viewport',
    ) as HTMLElement;

    if (vViewport) {
      vViewport.addEventListener('scroll', () => this.emitScrollChange());
    }

    if (hViewport) {
      hViewport.addEventListener('scroll', () => this.emitScrollChange());
    }
    this.gridOptions.onColumnResized = (event) => {
      if (this.columnKeyMap() && event.finished && event.column) {
        const colId = event.column.getColId();

        const logicalKey = Object.keys(this.columnKeyMap()).find((key) => this.columnKeyMap()[key] === colId);

        if (logicalKey) {
          const width = event.column.getActualWidth();
          this.columnResized.emit({ logicalKey, width });
        }
      }
    };
  }

  protected get showReset() {
    return this.filter() || this.sortable() || this.resizable();
  }

  protected get totalRowCount(): number {
    return Array.isArray(this.rowData()) ? this.rowData().length : 0;
  }

  protected get displayedRowCount(): number {
    return this.gridApi?.getDisplayedRowCount() ?? this.totalRowCount;
  }

  protected get showRowCount(): boolean {
    return !this.serverSide() && this.totalRowCount > 0;
  }

  public get selectedIds(): string[] {
    return this.selectedRows.map((x) => x.id);
  }

  public get selectedRows(): any[] {
    const selectedRows = [];
    this.forEachRow((node) => {
      if (node.isSelected()) {
        selectedRows.push(node.data);
      }
    });
    return selectedRows;
  }

  public updateRow(model: any): boolean {
    let updated = false;
    this.forEachRow((rowNode: RowNode) => {
      if (rowNode.data.id === model.id) {
        rowNode.setData(model);
        updated = true;
      }
    });

    return updated;
  }

  public get filteredRowData(): any[] {
    const filteredRowData = [];
    this.forEachRow((rowNode: RowNode) => {
      if (rowNode.data) {
        filteredRowData.push(rowNode.data);
      }
    });
    return filteredRowData;
  }

  public get sortedAndFilteredRowData(): any[] {
    const filteredRowData = [];
    this.forEachRowAfterFilterAndSort((rowNode: RowNode) => {
      if (rowNode.data) {
        filteredRowData.push(rowNode.data);
      }
    });
    return filteredRowData;
  }

  public get sortedAndFilteredRowIds(): string[] {
    const filteredRowIds = [];
    this.forEachRowAfterFilterAndSort((rowNode: RowNode) => {
      if (rowNode.data?.id) {
        filteredRowIds.push(rowNode.data.id);
      }
    });
    return filteredRowIds;
  }

  protected get initialRowData(): any[] {
    return this.serverSide() ? undefined : this.rowData();
  }

  protected get isEdited() {
    return this.edited;
  }

  private get userPreferenceKey() {
    return this.stateKey() ? `Grid.${this.stateKey()}` : '';
  }

  public resetEdited() {
    this.edited = false;
  }

  public export(fileName: string) {
    this.gridApi?.exportDataAsCsv({ fileName });
  }

  public loadState() {
    if (!this.stateKey()) {
      return;
    }

    this.updateFilter = false;
    this.userPreferenceService
      .get(this.userPreferenceKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe((userPreference: UserPreference) => {
        if (userPreference?.value) {
          this.state = JSON.parse(userPreference.value) as GridState;
        } else if (this.defaultState) {
          this.state = this.defaultState;
        } else {
          this.state = { columns: [], filter: undefined } as GridState;
        }
        this.updateFilter = true;
        this.applyState();
        this.stateLoaded = true;
      });
  }

  public applyState() {
    if (this.state) {
      this.gridColumnApi?.applyColumnState({ state: this.state.columns, applyOrder: true });
      this.gridApi?.setFilterModel(this.state.filter);
      this.updateFilterCount();
    }
  }

  private updateFilterCount() {
    const model = this.gridApi?.getFilterModel();
    this.filterCount = model ? Object.keys(model).length : 0;
  }

  public setFilter(filter: any) {
    const existingFilter = this.gridApi?.getFilterModel();
    this.gridApi?.setFilterModel({ ...existingFilter, ...filter });
  }

  public calculateAggregates() {
    if (this.columnDefs().some((x: ColDef) => x.aggFunc)) {
      const data = this.calculatePinnedBottomData();
      const rows = Object.keys(data).some((key) => key !== 'isAggregate' && data[key]) ? [data] : [];
      if (rows && this.showCumulativeTotals()) {
        const cumulativeData = this.calculateCumulativeData(data);
        rows.push(cumulativeData);
      }
      this.gridApi?.setPinnedBottomRowData(rows);
    }
  }

  public import(file: any) {
    this.importService()
      .import(file)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.imported.emit();
          this.notificationService.success($localize`Import completed successfully`);
        },
        error: (error: HttpErrorResponse) => this.notificationService.fail(error.error),
      });
  }

  public exportFile() {
    this.importService().export().pipe(takeUntil(this.destroy$)).subscribe();
  }

  public exportTemplateAndDataFile() {
    this.importService().exportTemplateAndData().pipe(takeUntil(this.destroy$)).subscribe();
  }

  public forEachRow(callback: (rowNode: RowNode) => void) {
    if (this.serverSide()) {
      this.gridApi?.forEachNode((rowNode: RowNode) => {
        if (rowNode.data) {
          callback(rowNode);
        }
      });
    } else {
      this.gridApi?.forEachNodeAfterFilter((rowNode: RowNode) => callback(rowNode));
    }
  }

  public refreshCells(nodes: IRowNode | IRowNode[], force = true) {
    this.gridApi.refreshCells({ rowNodes: Array.isArray(nodes) ? nodes : [nodes], force });
  }

  private forEachRowAfterFilterAndSort(callback: (rowNode: RowNode) => void) {
    if (this.serverSide()) {
      this.gridApi?.forEachNode((rowNode: RowNode) => {
        if (rowNode.data) {
          callback(rowNode);
        }
      });
    } else {
      this.gridApi?.forEachNodeAfterFilterAndSort((rowNode: RowNode) => callback(rowNode));
    }
  }

  protected clearSaveStateTimer() {
    if (this.saveStateTimer) {
      clearTimeout(this.saveStateTimer);
      this.saveStateTimer = undefined;
    }
  }

  protected onCellClicked(event: CellClickedEvent) {
    if (event.colDef.onCellClicked) {
      return;
    }

    if (this.chart()) {
      this.chartCellClicked.emit({
        rowIndex: event.rowIndex,
        colIndex: this.getColumnIndex(event),
      });
    } else {
      if (event.data.canEdit !== false && !event.colDef.editable) {
        this.edit.emit(event.data);
      }
    }
  }

  protected saveState(updateFilterState = false) {
    if (!this.stateKey() || !this.gridColumnApi || !this.gridApi || !this.stateLoaded) {
      return;
    }

    this.clearSaveStateTimer();
    this.saveStateTimer = setTimeout(() => {
      const previousState = JSON.stringify(this.state);
      const newColumns = this.gridColumnApi?.getColumnState() ?? [];
      const newColIds = new Set(newColumns.map((c) => c.colId));
      const previousColumns = this.state.columns ?? [];
      const merged = [...newColumns];
      let insertAfter = -1;
      for (const prevCol of previousColumns) {
        if (newColIds.has(prevCol.colId)) {
          insertAfter = merged.findIndex((c) => c.colId === prevCol.colId);
        } else {
          merged.splice(insertAfter + 1, 0, prevCol);
          insertAfter++;
        }
      }
      this.state.columns = merged;
      if (updateFilterState) {
        this.state.filter = this.gridApi?.getFilterModel();
      }
      const newState = JSON.stringify(this.state);
      if (newState !== previousState && (!this.showLayouts() || this.retainState())) {
        this.userPreferenceService.set(this.userPreferenceKey, newState).subscribe();
      }

      if (this.showLayouts()) {
        this.loadLayouts().subscribe();
      }

      this.calculateAggregates();
      this.savedState.emit();
    }, 100);
  }

  protected onRowDragMove(event: RowDragMoveEvent) {
    this.setEdited();
  }

  protected onRowSelected(event: RowSelectedEvent) {
    if (event.node.isSelected() === event.data.selected) {
      return;
    }
    this.setEdited();
    event.data.selected = event.node.isSelected();
    this.refreshCells(event.node);

    setTimeout(
      () =>
        this.rowSelected.emit({
          model: event.data,
          selected: event.node.isSelected(),
          rowIndex: event.rowIndex,
          node: event.node,
        }),
      event.rowIndex * 10,
    );
  }

  protected onFirstDataRendered(params: FirstDataRenderedEvent<any>) {
    setTimeout(() => this.firstDataRendered.emit(params));
  }

  protected gridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;

    if (!this.defaultState && this.gridColumnApi && this.gridApi) {
      this.defaultState = {
        columns: this.gridColumnApi.getColumnState(),
        filter: this.gridApi.getFilterModel(),
      } as GridState;
    }

    setTimeout(() => this.loadState(), 100);
  }

  protected cellValueChanged(event: CellValueChangedEvent) {
    this.setEdited();
    this.calculateAggregates();
  }

  protected rowDataUpdated() {
    setTimeout(() => {
      this.hideColumns();
      this.calculateAggregates();
      this.updateCheckboxSelection();
      this.applyState();
    }, 0);
  }

  protected filterChanged() {
    this.hideColumns();
    this.saveState(this.updateFilter);
    this.updateFilterCount();
    this.calculateAggregates();
  }

  protected resetColumns() {
    this.gridColumnApi?.resetColumnState();
  }

  protected resetFilters() {
    this.gridApi?.setFilterModel(undefined);
  }

  private getColumnIndex(event: CellClickedEvent): number {
    const field = event.colDef.field;
    return event.columnApi.getColumns()?.findIndex((col) => col.getColDef().field === field);
  }

  private setEdited() {
    if (this.save$.observed && this.showDiscard()) {
      this.edited = true;
    }
  }

  private hideColumns() {
    if (!this.columnDefs().some((col: ColDef | any) => col.showColumnFunc)) {
      return;
    }

    this.columnDefs().forEach((col: ColDef | any) => {
      if (col.showColumnFunc) {
        this.gridColumnApi?.setColumnVisible(col.field, col.showColumnFunc(this.filteredRowData));
      }
    });

    this.state.columns = this.gridColumnApi?.getColumnState();
  }

  private calculatePinnedBottomData() {
    const data = {
      isAggregate: true,
    };

    this.columnDefs().forEach((col: ColDef | any) => {
      if (col.showAggFunc && !col.showAggFunc(this.filteredRowData)) {
        return;
      }

      const field = col.field;
      if (col.customAggFunc) {
        data[field] = col.customAggFunc(this.filteredRowData);
        return;
      }

      switch (col.aggFunc) {
        case AggFunc.avg:
          data[field] = this.formatService.number(this.average(col, field, false));
          break;
        case AggFunc.count:
          data[field] = this.formatService.number(this.count(), 0);
          break;
        case AggFunc.first:
          break;
        case AggFunc.sum:
          const sum = this.sum(col, field, false);
          if (this.showCumulativeTotals()) {
            data[`${field}Sum`] = sum;
          }
          data[field] = this.formatService.number(sum);
          break;
        case AggFunc.sumInteger:
          const sumInt = this.sum(col, field, false);
          if (this.showCumulativeTotals()) {
            data[`${field}Sum`] = sumInt;
          }
          data[field] = this.formatService.number(sumInt, 0);
          break;
      }

      switch (col.groupAggFunc) {
        case AggFunc.avg:
          data[field] = this.formatService.number(this.average(col, field, true), 0);
          break;
        case AggFunc.count:
          data[field] = this.formatService.number(this.count(), 0);
          break;
        case AggFunc.first:
          break;
        case AggFunc.sum:
          data[field] = this.formatService.number(this.sum(col, field, true));
          break;
        case AggFunc.sumInteger:
          data[field] = this.formatService.number(this.sum(col, field, true), 0);
          break;
      }
    });

    return data;
  }

  private calculateCumulativeData(data: any) {
    const cumulativeData = {
      isAggregate: true,
    };

    let total = 0;
    this.columnDefs().forEach((col: ColDef | any) => {
      const num = data[`${col.field}Sum`];
      if (!isNaN(num)) {
        if (!col.ignoreCumulativeColumn) {
          total += num;
        }
        cumulativeData[col.field] = this.formatService.number(total);
      }
    });

    return cumulativeData;
  }

  private count(): number {
    let count = 0;
    this.forEachRow(() => count++);
    return count;
  }

  private sum(col: any, field: any, group: boolean): number {
    const calculation = col.calculation;
    const groupField = group ? this.findRowGroupField() : undefined;
    const seenKeys = groupField ? new Set<any>() : undefined;
    let sum = 0;

    const accumulate = (data: any) => {
      if (!data) {
        return;
      }
      if (groupField && seenKeys) {
        const key = data[groupField];
        if (seenKeys.has(key)) {
          return;
        }
        seenKeys.add(key);
      }
      const value = calculation ? calculation(data) : data[field];
      if (value) {
        sum += this.formatService.parseNumberString(value);
      }
    };

    this.forEachRow((rowNode: RowNode) => accumulate(rowNode.data));
    return sum;
  }

  private findRowGroupField(): string | undefined {
    const groupCol = (this.columnDefs() ?? []).find((c: any) => c.rowGroup) as any;
    return groupCol?.field;
  }

  private average(col: any, field: any, group: boolean): number {
    const sum = this.sum(col, field, group);
    return this.count() ? sum / this.count() : 0;
  }

  public updateCheckboxSelection() {
    setTimeout(() => this.forEachRow((rowNode: RowNode) => rowNode.setSelected(rowNode.data?.selected ?? false)), 0);
  }

  public clearCheckboxSelection(index: number) {
    this.forEachRow((rowNode: RowNode) => {
      if (rowNode.data && rowNode.rowIndex === index) {
        rowNode.data.selected = false;
      }
    });
    this.updateCheckboxSelection();
  }

  public scrollToRatio(scrollChanged: ScrollChanged) {
    const vViewport = this.agGridRef().nativeElement.querySelector('.ag-body-viewport') as HTMLElement;
    const hViewport = this.agGridRef().nativeElement.querySelector(
      '.ag-body-horizontal-scroll-viewport',
    ) as HTMLElement;

    if (vViewport) {
      const maxScrollTop = vViewport.scrollHeight - vViewport.clientHeight;
      vViewport.scrollTop = scrollChanged.top * maxScrollTop;
    }

    if (hViewport) {
      const maxScrollLeft = hViewport.scrollWidth - hViewport.clientWidth;
      hViewport.scrollLeft = scrollChanged.left * maxScrollLeft;
    }
  }

  emitScrollChange() {
    const vViewport = this.agGridRef().nativeElement.querySelector('.ag-body-viewport') as HTMLElement;
    const hViewport = this.agGridRef().nativeElement.querySelector(
      '.ag-body-horizontal-scroll-viewport',
    ) as HTMLElement;

    const topRatio = vViewport ? vViewport.scrollTop / (vViewport.scrollHeight - vViewport.clientHeight || 1) : 0;

    const leftRatio = hViewport ? hViewport.scrollLeft / (hViewport.scrollWidth - hViewport.clientWidth || 1) : 0;

    this.scrollChanged.emit({ top: topRatio, left: leftRatio });
  }

  public setColumnWidth(colId: string, width: number) {
    if (this.gridApi && this.gridApi.getColumnDef(colId)) {
      this.gridColumnApi.setColumnWidth(colId, width, false); // false = no shift of other columns
    }
  }

  public loadLayouts() {
    return this.userPreferenceService.search(this.layoutUserPreferenceKey).pipe(
      takeUntil(this.destroy$),
      tap((userPreferences: UserPreference[]) => {
        this.layouts$.next(userPreferences);
        this.layoutsOptions$.next(
          this.utilityService.sort(
            userPreferences.map(
              (userPreference: UserPreference): IdDescription => ({
                id: userPreference.id,
                description: userPreference.key.includes(this.layoutUserPreferenceKeyGlobal)
                  ? `${userPreference.key.replace(this.layoutUserPreferenceKeyGlobal, '')} (${this.globalText})`
                  : userPreference.key.replace(this.layoutUserPreferenceKey, ''),
              }),
            ),
            (option: IdDescription) => option.description,
          ),
        );
      }),
    );
  }

  public get layouts(): UserPreference[] {
    return this.layouts$.value;
  }

  public saveCurrentStateAsLayout(global: boolean, name: string): Observable<string> {
    const key = `${global ? this.layoutUserPreferenceKeyGlobal : this.layoutUserPreferenceKey}${name}`;

    return this.userPreferenceService.set(key, JSON.stringify(this.state)).pipe(
      takeUntil(this.destroy$),
      tap(() => this.loadLayouts().subscribe()),
      map((savedPreference: UserPreference) => savedPreference.id),
    );
  }

  public updateCurrentLayout(): Observable<any> {
    const layoutKey = this.layoutPreferenceKey ?? this.layoutStateKey();
    if (!layoutKey) {
      return undefined;
    }

    return this.userPreferenceService.set(layoutKey, JSON.stringify(this.state)).pipe(
      takeUntil(this.destroy$),
      tap(() => this.loadLayouts().subscribe()),
    );
  }

  public deleteLayout(layoutId: string) {
    return this.userPreferenceService.delete(layoutId).pipe(
      takeUntil(this.destroy$),
      tap(() => {
        this.layoutPreferenceKey = undefined;
        this.loadLayouts().subscribe();
      }),
    );
  }

  public canDeleteLayout(layoutId: string): boolean {
    const preference = this.layouts$.value.find((userPreference: UserPreference) => userPreference.id === layoutId);
    return !!preference && preference.canDelete !== false;
  }

  public applyLayout(layoutId?: string) {
    if (layoutId) {
      const layout = this.layouts$.value.find((x) => x.id === layoutId);
      if (!layout) {
        return;
      }

      this.layoutPreferenceKey = layout.key;
      this.state = JSON.parse(layout.value) as GridState;
      this.applyState();
    } else {
      this.loadState();
    }
  }
  public getLayoutFields(): FormlyFieldConfig[] {
    if (!this.layoutFields) {
      this.layoutFields = [
        this.formlyService.text('name', $localize`Layout Name`, { required: true, maxLength: 100 }),
        this.formlyService.checkbox('global', $localize`Global to all users`),
      ];
    }
    return this.layoutFields;
  }

  private get layoutUserPreferenceKey(): string {
    return this.stateKey() ? `Layout.${this.stateKey()}.` : '';
  }

  private get layoutUserPreferenceKeyGlobal(): string {
    return `${this.layoutUserPreferenceKey}Global.`;
  }
}
